<script lang="ts" setup>
import { storeToRefs } from 'pinia';

import ClovyrIcon from '@clovyr/bed/assets/images/clovyr-icon.svg';
import AppLogo from '@clovyr/bed/components/elements/AppLogo.vue';
import { DeploymentAddon } from '@clovyr/pollen/types/Deployment';

import CheckboxBlock from '@/components/blocks/CheckboxBlock.vue';
import { useLauncherStore } from '@/stores/launcher_store';
import { LauncherState } from '@/stores/types';

type Props = {
  isReview?: boolean;
};
const props = defineProps<Props>();

const launcherStore = useLauncherStore();
const { toggleAddon } = launcherStore;
const { deployment, status } = storeToRefs(launcherStore);

const isEditable = computed(() => status.value !== LauncherState.Review);

const isBackupsSelected = computed(() =>
  deployment.value.addons?.includes(DeploymentAddon.Backups),
);

function onBackupInput(checked: boolean) {
  toggleAddon(DeploymentAddon.Backups, checked);
}
</script>

<template>
  <div class="add-on-block">
    <div class="setup-controls-heading launch-heading--secondary">Add ons:</div>
    <div class="section-highlighted background-gradient--base">
      <div class="backup-block__top">
        <AppLogo :logo="{ logoUrl: ClovyrIcon }" size="medium" />
        <div class="backup-block__text text--h4">By Clovyr</div>
      </div>
      <div class="backup-block__text-body">
        Keep your work safe with Clovyr's backup service - automatic, reliable data protection that
        creates regular snapshots of your applications so you can focus on building without worrying
        about data loss.
      </div>
      <div class="check-block">
        <CheckboxBlock
          :isToggleSwitch="true"
          :value="isBackupsSelected"
          :disabled="!isEditable"
          type="checkbox"
          shape="rounded"
          title="Pay $5/mo for backup service"
          @update:modelValue="onBackupInput"
        />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use 'styles/core/colors';
@use 'styles/core/layout/grid-vars';
@use 'styles/core/typography/type';
@use 'styles/core/util';
@use '@clovyr/bed/styles/vendor/bootstrap/scss/mixins/breakpoints' as *;

.add-on-block {
  margin-top: 2rem;
  margin-bottom: 3rem;

  .setup-controls-heading {
    @include type.text--h4;

    margin: 0 0 0.5rem 1rem;
    color: colors.color(white);

    @include media-breakpoint-up(sm) {
      margin-top: 2rem;
    }
  }

  .backup-block {
    &__top {
      display: flex;
      justify-content: left;
      align-items: center;
      margin-bottom: grid-vars.space(2);
    }

    &__text {
      margin-left: grid-vars.space(2);
    }

    &__text-body {
      padding: 1rem;
    }
  }

  .completed-item {
    display: flex;
    justify-content: space-between;
    padding: grid-vars.space(1.8) grid-vars.space(2.4);
    border: 1px solid rgb(132 115 152 / 30%);

    @include util.borders-rounded;
  }
}
</style>
